import { z } from 'zod';

import { authStoreForm } from '@/pages/store-management/auth-stores/service';
import {
  FormError,
  validatePhoneNumberFormat,
  validateZipCodeFormat,
  validBlank,
  validMaxStr,
  validNumInRange,
} from '@/validate';
import { validNum } from '@/validate/number';

export type AuthStoreFormError = {
  name: FormError;
  email: FormError;
  password: FormError;
  confirmPassword: FormError;
  phoneNumber: FormError;
  prefecture: FormError;
  city: FormError;
  streetAddress: FormError;
  building: FormError;
  zipCode: FormError;
  latitude: FormError;
  longitude: FormError;
};

const { MAX_255 } = validNum;

export const validateName = (
  name: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.name.isTouched = true;
  const label = authStoreForm.name.label;
  const isMax = validMaxStr.check(name, MAX_255);
  const isBlank = validBlank.check(name);
  if (isBlank) error.name.message = validBlank.message(label);
  if (isMax) error.name.message = validMaxStr.message(label, MAX_255);
  error.name.isError = isBlank || isMax;
  return error;
};

export const validateEmail = (
  email: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.email.isTouched = true;
  const label = authStoreForm.email.label;
  const isMax = validMaxStr.check(email, MAX_255);
  const isBlank = validBlank.check(email);
  if (isBlank) error.email.message = validBlank.message(label);
  if (isMax) error.email.message = validMaxStr.message(label, MAX_255);
  error.email.isError = isBlank || isMax;
  return error;
};

export const validatePassword = (
  password: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.password.isTouched = true;
  const label = authStoreForm.password.label;
  const isMax = validMaxStr.check(password, MAX_255);
  const isBlank = validBlank.check(password);
  if (isBlank) error.password.message = validBlank.message(label);
  if (isMax) error.password.message = validMaxStr.message(label, MAX_255);
  error.password.isError = isBlank || isMax;
  return error;
};

export const validateConfirmPassword = (
  confirmPassword: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.confirmPassword.isTouched = true;
  const label = authStoreForm.confirmPassword.label;
  const isMax = validMaxStr.check(confirmPassword, MAX_255);
  const isBlank = validBlank.check(confirmPassword);
  if (isBlank) error.confirmPassword.message = validBlank.message(label);
  if (isMax) {
    error.confirmPassword.message = validMaxStr.message(label, MAX_255);
  }
  error.confirmPassword.isError = isBlank || isMax;
  return error;
};

export const validateZipCode = (
  zipCode: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.zipCode.isTouched = true;
  const isBlank = validBlank.check(zipCode);
  const label = authStoreForm.zipCode.label;
  const isValidateFormat = validateZipCodeFormat.check(zipCode);
  if (isValidateFormat) {
    error.zipCode.message = validateZipCodeFormat.message(label);
  }
  if (isBlank) error.zipCode.message = validBlank.message(label);
  error.zipCode.isError = isBlank || isValidateFormat;
  return error;
};

export const validatePhoneNumber = (
  phoneNumber: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.phoneNumber.isTouched = true;
  const label = authStoreForm.phoneNumber.label;
  const isValidatePhoneNumberFormat =
    validatePhoneNumberFormat.check(phoneNumber);
  if (isValidatePhoneNumberFormat)
    error.phoneNumber.message = validatePhoneNumberFormat.message(label);
  error.phoneNumber.isError = isValidatePhoneNumberFormat;
  return error;
};

export const validatePrefecture = (
  prefecture: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.prefecture.isTouched = true;
  const label = authStoreForm.prefecture.label;
  const isBlank = validBlank.check(prefecture);
  if (isBlank) error.prefecture.message = validBlank.message(label);
  error.prefecture.isError = isBlank;
  return error;
};

export const validateCity = (
  city: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.city.isTouched = true;
  const label = authStoreForm.city.label;
  const isMax = validMaxStr.check(city, MAX_255);
  const isBlank = validBlank.check(city);
  if (isBlank) error.city.message = validBlank.message(label);
  if (isMax) error.city.message = validMaxStr.message(label, MAX_255);
  error.city.isError = isBlank || isMax;
  return error;
};

export const validateStreetAddress = (
  streetAddress: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.streetAddress.isTouched = true;
  const label = authStoreForm.streetAddress.label;
  const isMax = validMaxStr.check(streetAddress, MAX_255);
  const isBlank = validBlank.check(streetAddress);
  if (isBlank) error.streetAddress.message = validBlank.message(label);
  if (isMax) error.streetAddress.message = validMaxStr.message(label, MAX_255);
  error.streetAddress.isError = isBlank || isMax;
  return error;
};

const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

export const validateBuilding = (
  building: string,
  error: AuthStoreFormError
): AuthStoreFormError => {
  error.building.isTouched = true;
  const label = authStoreForm.building.label;
  const isMax = validMaxStr.check(building, MAX_255);
  if (isMax) error.building.message = validMaxStr.message(label, MAX_255);
  error.building.isError = isMax;
  return error;
};

export const validateLatitude = (
  latitude: number | null,
  error: AuthStoreFormError
): AuthStoreFormError => {
  const isNumInRange = validNumInRange.check(
    latitude ?? 0,
    MIN_LATITUDE,
    MAX_LATITUDE
  );
  if (isNumInRange) {
    error.latitude.message = validNumInRange.message(
      authStoreForm.latitude.label,
      MIN_LATITUDE,
      MAX_LATITUDE
    );
  }
  error.latitude.isError = isNumInRange;
  error.latitude.isTouched = true;
  return error;
};

export const validateLongitude = (
  longitude: number | null,
  error: AuthStoreFormError
): AuthStoreFormError => {
  const isNumInRange = validNumInRange.check(
    longitude ?? 0,
    MIN_LONGITUDE,
    MAX_LONGITUDE
  );
  if (isNumInRange) {
    error.longitude.message = validNumInRange.message(
      authStoreForm.longitude.label,
      MIN_LONGITUDE,
      MAX_LONGITUDE
    );
  }
  error.longitude.isError = isNumInRange;
  error.longitude.isTouched = true;
  return error;
};

export const validateAuthStorePassword = z
  .string({
    required_error: '新しいパスワードを入力してください',
  })
  .min(8, {
    message: 'パスワードは8文字以上で入力してください',
  })
  .max(20, {
    message: 'パスワードは20文字以下で入力してください',
  });
